import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { AuthService } from '../services/auth/auth.service';
import { firstValueFrom } from 'rxjs';

type SubscriptionState = {
  hasActiveSubscription: boolean | null;
  lastCheckedAt: number | null; // epoch ms
  isChecking: boolean;
  error: string | null;
  complimentaryAccessEnd: number | null;
};

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

const initialState: SubscriptionState = {
  hasActiveSubscription: null,
  lastCheckedAt: null,
  isChecking: false,
  error: null,
  complimentaryAccessEnd: null,
};

/**
 * Keeps the app-shell indicator tied to the existing server-authoritative
 * subscription response. The browser never infers Complimentary Access from
 * a trial status, price, or locally maintained start date.
 */
function getComplimentaryAccessEnd(subscription: unknown): number | null {
  if (!subscription || typeof subscription !== 'object') return null;
  const candidate = subscription as {
    is_complimentary_access?: unknown;
    current_period_end?: unknown;
  };
  const end = candidate.current_period_end;
  return candidate.is_complimentary_access === true &&
    typeof end === 'number' &&
    Number.isFinite(end) &&
    end * 1000 > Date.now()
    ? end
    : null;
}

export const SubscriptionStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store, authService = inject(AuthService)) => {
    return {
      /**
       * Get cached value if fresh; otherwise refresh from API with TTL.
       * Returns boolean if known, otherwise triggers fetch and returns null until resolved.
       */
      async ensureFresh(
        ttlMs: number = DEFAULT_TTL_MS,
      ): Promise<boolean | null> {
        const now = Date.now();
        const last = store.lastCheckedAt();
        const withinTtl = !!last && now - last < ttlMs;
        if (withinTtl && store.hasActiveSubscription() !== null) {
          return store.hasActiveSubscription();
        }

        if (store.isChecking()) {
          // Another live request means the cached value is no longer a
          // definitive entitlement decision. Let the guard use recovery UX
          // instead of routing a potentially-paid customer to Checkout.
          return null;
        }

        patchState(store, { isChecking: true, error: null });
        try {
          const res = await firstValueFrom(
            authService.checkSubscriptionStatus(),
          );
          if (typeof res?.hasActiveSubscription !== 'boolean') {
            patchState(store, {
              hasActiveSubscription: null,
              isChecking: false,
              lastCheckedAt: Date.now(),
              error: 'Invalid subscription status response',
              complimentaryAccessEnd: null,
            });
            return null;
          }
          patchState(store, {
            hasActiveSubscription: res.hasActiveSubscription,
            lastCheckedAt: Date.now(),
            isChecking: false,
            error: null,
            complimentaryAccessEnd: res.hasActiveSubscription
              ? getComplimentaryAccessEnd(res.subscription)
              : null,
          });
          return store.hasActiveSubscription();
        } catch (e: any) {
          patchState(store, {
            hasActiveSubscription: null,
            isChecking: false,
            // Keep the failure timestamp for diagnostics. A null result is
            // intentionally never treated as a fresh entitlement decision,
            // so the next guarded navigation retries the live check.
            lastCheckedAt: Date.now(),
            error: e?.message ?? 'Failed to check subscription',
            complimentaryAccessEnd: null,
          });
          return null;
        }
      },
      /**
       * Force refresh, bypassing TTL.
       */
      async refresh(): Promise<boolean | null> {
        patchState(store, { lastCheckedAt: null });
        return this.ensureFresh(0);
      },
      /**
       * Apply a known result from an authoritative subscription response.
       * Change notifications must still use refresh() so the server, rather
       * than a browser payload, remains the entitlement authority.
       */
      applyAuthoritativeEntitlement(
        hasActiveSubscription: boolean,
        subscription: unknown = null,
      ): void {
        patchState(store, {
          hasActiveSubscription,
          lastCheckedAt: Date.now(),
          error: null,
          complimentaryAccessEnd: hasActiveSubscription
            ? getComplimentaryAccessEnd(subscription)
            : null,
        });
      },
      /**
       * Configure TTL externally if needed by resetting timestamp.
       */
      invalidateCache(): void {
        patchState(store, { lastCheckedAt: null });
      },
      reset(): void {
        patchState(store, initialState);
      },
    };
  }),
);
